/**
 * Ligação do motor de sync ao op-sqlite: dreno da outbox local → oplog, e aplicação de
 * mensagens remotas ao banco. É a "camada de cima" que outbox.ts/engine.ts mencionam — a parte
 * que toca o SQLite de verdade. Ver docs/features/SYNC.md § 3.
 *
 * NÃO testável no Jest (op-sqlite nativo) — verificada pelo harness no device (selfTest.ts).
 */

import { execute, runInTransaction } from '@/db/client';
import { captureSpecs, withCaptureSuspended, type CaptureTableSpec } from './capture';
import { materialize, type MaterializedRow } from './engine';
import { type Hlc } from './hlc';
import { type Message } from './messages';
import { outboxRowsToMessages, type OutboxRow } from './outbox';
import { loadMessages, persistHlc, persistMessages } from './persistence';

const specByTable = new Map<string, CaptureTableSpec>(captureSpecs().map((s) => [s.table, s]));

/**
 * Drena a outbox local → oplog (`sync_messages`), carimbando HLC em ordem de escrita. Tudo numa
 * transação: converte, grava no oplog COM `pending_upload=1`, apaga as linhas drenadas e persiste
 * o relógio — ou nada (rollback). Idempotente por construção (o oplog dedupa por messageKey; as
 * linhas drenadas somem da outbox).
 *
 * Drenar NÃO significa "enviado": as mensagens ficam pendentes até o ciclo confirmar o upload no
 * Drive (`clearPendingUpload`). Antes, o dreno apagava a outbox e o upload vinha depois — uma
 * falha de rede fazia o lote sumir da outbox sem nunca subir: os peers divergiam PARA SEMPRE, em
 * silêncio, e o ciclo seguinte reportava ok. Quem quer o lote a subir usa `loadPendingUpload()`.
 */
export async function drainOutbox(hlc: Hlc): Promise<Message[]> {
    return runInTransaction(async () => {
        const res = await execute(
            'SELECT seq, table_name, row_id, column_name, value FROM sync_outbox ORDER BY seq',
        );
        const rows = res.rows as unknown as OutboxRow[];
        if (rows.length === 0) return [];

        const messages = outboxRowsToMessages(rows, hlc);
        await persistMessages(messages, { pendingUpload: true });

        const maxSeq = Math.max(...rows.map((r) => Number(r.seq)));
        await execute('DELETE FROM sync_outbox WHERE seq <= ?', [maxSeq]);
        await persistHlc(hlc);
        return messages;
    });
}

/**
 * Aplica mensagens REMOTAS (de um peer) ao banco. Integra o relógio (não regride), grava no
 * oplog, materializa o estado das linhas TOCADAS por este lote (LWW sobre o oplog completo) e
 * reescreve só essas linhas nas tabelas reais — com a captura SUSPENSA (guard anti-eco), pra não
 * re-emitir como escrita local. Idempotente (INSERT OR IGNORE + LWW convergente).
 *
 * Pré-condição: a outbox local já foi drenada (senão uma escrita local ainda não no oplog seria
 * sobrescrita pela materialização). O orquestrador chama drainOutbox() antes.
 */
export async function applyRemoteMessages(hlc: Hlc, incoming: Message[]): Promise<number> {
    if (incoming.length === 0) return 0;
    for (const m of incoming) hlc.recv(m.timestamp);

    let skipped = 0;

    // FK OFF via opção do runInTransaction (aplicado SOB o mutex, antes do BEGIN): o lote pode
    // chegar fora de ordem de dependência (o filho antes do pai). Executar o PRAGMA AQUI fora
    // não funciona — se houver transação em voo na conexão (escrita do usuário), o SQLite o
    // ignora em silêncio, o apply roda com FK LIGADO, a linha-filho estoura FK, o savepoint a
    // pula e o cursor avança = perda permanente e silenciosa daquela linha neste aparelho.
    await runInTransaction(async () => {
        await persistMessages(incoming);
        await persistHlc(hlc);

        const state = materialize(await loadMessages());
        const touched = new Set(incoming.map((m) => `${m.table} ${m.row}`));

        await withCaptureSuspended(execute, async () => {
            for (const key of touched) {
                const sep = key.indexOf(' ');
                const table = key.slice(0, sep);
                const row = key.slice(sep + 1);
                // SAVEPOINT por linha: FK OFF não desliga ÍNDICE ÚNICO, e dois aparelhos
                // criam de forma independente linhas que colidem em unique colateral —
                // ex.: fatura auto-criada pro MESMO (cartão, mês) com ids diferentes,
                // tag/pessoa com o mesmo nome. Sem isto, UMA linha-veneno abortava o
                // apply inteiro, o cursor não avançava e TODO ciclo futuro baixava o
                // mesmo lote e morria no mesmo ponto — sync travado pra sempre. Pular só
                // a linha em conflito limita o dano (divergência localizada) e mantém o
                // resto do lote aplicado.
                await execute('SAVEPOINT apply_row');
                try {
                    await writeRow(table, row, state[table]?.[row]);
                    await execute('RELEASE SAVEPOINT apply_row');
                } catch (e) {
                    await execute('ROLLBACK TO SAVEPOINT apply_row').catch(() => {});
                    await execute('RELEASE SAVEPOINT apply_row').catch(() => {});
                    skipped += 1;
                    console.warn(`[sync] linha pulada no apply (${table} ${row}):`, e);
                }
            }
        });
    }, { foreignKeys: 'off' });
    return skipped;
}

/**
 * Reescreve UMA linha materializada na tabela real (upsert), ou a apaga se sumiu do estado
 * (tombstone vencedor). `data` traz só colunas de dado — a identidade vem do row_id.
 * Chamar com a captura SUSPENSA: é dado que veio de outro aparelho.
 */
export async function writeRow(table: string, row: string, data: MaterializedRow | undefined): Promise<void> {
    const spec = specByTable.get(table);
    if (!spec) return; // tabela não sincronizada — ignora

    if (!data) {
        await deleteRow(spec, row);
        return;
    }

    const cols: string[] = [];
    const vals: (string | number | null)[] = [];

    // Regular: o id é o próprio row_id (não viaja como célula). Junction: as colunas-chave
    // viajam como células, então já estão em `data`.
    if (spec.rowKey.length === 1) {
        cols.push(spec.rowKey[0]);
        vals.push(row);
    }
    for (const [col, value] of Object.entries(data)) {
        cols.push(col);
        vals.push((value as string | number | null) ?? null);
    }

    const colList = cols.map((c) => `\`${c}\``).join(', ');
    const placeholders = cols.map(() => '?').join(', ');
    const conflictTarget = spec.rowKey.map((c) => `\`${c}\``).join(', ');
    const updates = cols
        .filter((c) => !spec.rowKey.includes(c))
        .map((c) => `\`${c}\` = excluded.\`${c}\``)
        .join(', ');

    // Junction sem colunas mutáveis (só o par-chave) → nada a atualizar no conflito.
    const onConflict = updates ? `DO UPDATE SET ${updates}` : 'DO NOTHING';
    await execute(
        `INSERT INTO \`${table}\` (${colList}) VALUES (${placeholders}) ON CONFLICT(${conflictTarget}) ${onConflict}`,
        vals,
    );
}

async function deleteRow(spec: CaptureTableSpec, row: string): Promise<void> {
    if (spec.rowKey.length === 1) {
        await execute(`DELETE FROM \`${spec.table}\` WHERE \`${spec.rowKey[0]}\` = ?`, [row]);
        return;
    }
    // Junction: row_id composto 'a:b' → separa nas colunas-chave (ids são hex, sem ':').
    const parts = row.split(':');
    const where = spec.rowKey.map((c) => `\`${c}\` = ?`).join(' AND ');
    await execute(`DELETE FROM \`${spec.table}\` WHERE ${where}`, parts);
}
